/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { Typography, Form, Row, Col, Input, notification, Button } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import get from 'lodash/get';
import PipelineDetails from './PipelineDetails';
import AddNewPipeline from './AddNewPipeline';
import AddPQTReportModal from './AddPQTReportModal';
import { fetchSowDetails, addSowDetails, updateSowDetails } from '../redux/sowDetails/actions';
import {
  fetchPqtElements,
  fetchProjectPqtReport,
  updateProjectPqtReport,
  createProjectPqtReport,
  createPqtElement,
} from '../redux/projectPqtReports/actions';
import { formatExpectedLength, sumProjectExpectedLength } from '../utils/sowExpectedLength';
import { buildSowMeasurementApiFields } from '../utils/sowApiFields';

const { Title, Text } = Typography;

const Details = (props) => {
  const {
    selectedProject,
    onFetchSowDetails,
    sowDetails,
    onAddSowDetails,
    onUpdateSowDetails,
    projectTeamMembersAccessList,
    userAccessList,
    pqtElements,
    pqtReport,
    isPqtReportLoading,
    isReportFetching,
    onFetchPqtElements,
    onFetchProjectPqtReport,
    onUpdateProjectPqtReport,
    onCreateProjectPqtReport,
    onCreatePqtElement,
  } = props;

  const [editDetailsForm] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isPqtReportModalVisible, setIsPqtReportModalVisible] = useState(false);
  const [pipelineData, setPipelineData] = useState([]);
  const [editValue, setEditValue] = useState(false);
  const [keyValue, setKeyValue] = useState(0);
  const [checked, setChecked] = useState(true);
  const [restrict, setRestrict] = useState(false);
  const [searchKeyExist, setSearchKeyExist] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [uniquePipeId, setUniquePipeId] = useState('');
  const [editingSowRecord, setEditingSowRecord] = useState(null);

  // Dedupe GET /sows within a short window (React StrictMode double-mount + same project re-renders)
  const sowFetchDedupeRef = useRef({ id: undefined, at: 0 });

  useEffect(() => {
    const projId = get(selectedProject, 'id', undefined);
    if (projId == null) return;
    const now = Date.now();
    const prev = sowFetchDedupeRef.current;
    if (prev.id === projId && now - prev.at < 800) {
      return;
    }
    sowFetchDedupeRef.current = { id: projId, at: now };
    onFetchSowDetails(projId);
  }, [selectedProject?.id, onFetchSowDetails]);

  useEffect(() => {
    const list = Array.isArray(sowDetails) ? sowDetails : [];
    // Drop phantom rows from API (empty pipeline section still rendered as a table row)
    const cleaned = list.filter(
      (item) => item && String(item.pipelineSection ?? '').trim() !== ''
    );
    setPipelineData(cleaned);
    setFilteredData(cleaned);
  }, [sowDetails]);

  const projectExpectedLength = useMemo(
    () => sumProjectExpectedLength(pipelineData),
    [pipelineData]
  );

  const onSave = async (
    pipelineSec,
    material,
    plannedWelds,
    plannedWorks,
    stalkId,
    fieldJointType,
    blastStandard,
    pipeStringId,
    id,
    odInches,
    wallThicknessMm,
    expectedPipeLength,
    productionLine,
    actualPipelineLength,
    actualWelds
  ) => {
    const measurement = buildSowMeasurementApiFields(odInches, wallThicknessMm, expectedPipeLength);
    const pl =
      productionLine && typeof productionLine === 'object' && Object.keys(productionLine).length > 0
        ? productionLine
        : {};
    const actualFields = {};
    if (actualPipelineLength != null) actualFields.actualPipelineLength = actualPipelineLength;
    if (actualWelds != null) actualFields.actualWelds = actualWelds;

    if (!editValue && id === 0) {
      const projectId = get(selectedProject, 'id', undefined);
      var obj = {
        pipelineSection: pipelineSec,
        material,
        plannedWelds,
        plannedWorks,
        stalkId,
        fieldJointType,
        blastStandard,
        pipeStringId,
        projectId,
        totalWeldsCount: 0,
        ...measurement,
        ...pl,
        ...actualFields,
      };
      await onAddSowDetails(obj);
    } else {
      var sowId = sessionStorage.getItem('sowId');
      var projectId = sessionStorage.getItem('projectId');

      var obj = {
        id: parseInt(sowId),
        projectId: parseInt(projectId),
        pipelineSection: pipelineSec,
        material: material,
        plannedWelds: plannedWelds,
        plannedWorks: plannedWorks,
        stalkId: stalkId,
        fieldJointType: fieldJointType,
        blastStandard: blastStandard,
        pipeStringId: pipeStringId,
        ...measurement,
        ...pl,
        ...actualFields,
      };
      await onUpdateSowDetails({ data: obj });
    }
    setTimeout(() => {
      const projId = get(selectedProject, 'id', undefined);
      if (projId) {
        onFetchSowDetails(projId);
      }
    }, 2000);
  };

  const onPipelineSearch = (event) => {
    const searchKey = event.target.value.trim().toLowerCase();
    setSearchKeyExist(searchKey);

    if (searchKey) {
      const filteredData = pipelineData.filter((item) =>
        item.pipelineSection.toLowerCase().includes(searchKey)
      );
      setFilteredData(filteredData);
    } else {
      setFilteredData(pipelineData);
    }
  };

  //details write access control for normal user
  const isButtonDisabled = () => {
    if (userAccessList.includes('admin') || userAccessList.includes('super-admin')) return false;
    else if (projectTeamMembersAccessList[0]?.isGuestUser) {
      return true;
    } else if (
      projectTeamMembersAccessList[0]?.isNormalUser &&
      !projectTeamMembersAccessList[0]?.tabPermissions?.Details?.write
    ) {
      return true;
    } else {
      return false;
    }
  };
  const isWriteAccess = isButtonDisabled();

  const handleSavePqtReport = async (payload) => {
    const projectId = get(selectedProject, 'id', undefined);
    if (payload.reportId) {
      await onUpdateProjectPqtReport({
        reportId: payload.reportId,
        name: payload.name,
        description: payload.description,
        elements: payload.elements,
      });
    } else {
      await onCreateProjectPqtReport({
        projectId,
        name: payload.name,
        description: payload.description,
        elements: payload.elements,
      });
    }
    setTimeout(() => {
      if (projectId) {
        onFetchProjectPqtReport(projectId);
      }
      setIsPqtReportModalVisible(false);
    }, 1500);
  };

  return (
    <div className="d-flex h-100">
      <div
        className="flex-fill custom-fill overflow-auto"
        style={{ maxWidth: '80%' }}
      >
        <div className="d-flex flex-column h-100">
          <div className="flex-auto">
            <div className="d-flex mb-3 align-items-center tab-content-title">
              <Title
                className="mb-0"
                level={4}
              >
                Statement of Work
              </Title>
              <div className="searchConsumabels">
                <Input
                  placeholder="Search By Pipeline Section"
                  onChange={onPipelineSearch}
                  value={searchKeyExist}
                  allowClear
                />
              </div>
              <div
                className="ps-3 add-new-pipeline add-new-pipeline--static"
                style={{
                  marginLeft: 16,
                  flexShrink: 0,
                }}
              >
                Project expected length
                {(() => {
                  const raw =
                    projectExpectedLength != null && Number.isFinite(projectExpectedLength)
                      ? projectExpectedLength
                      : 0;
                  return ` — ${formatExpectedLength(raw)}`;
                })()}
              </div>
              <AddNewPipeline
                className="details-modal"
                setIsModalVisible={setIsModalVisible}
                isModalVisible={isModalVisible}
                editDetailsForm={editDetailsForm}
                onSave={onSave}
                editValue={editValue}
                setEditValue={setEditValue}
                keyValue={keyValue}
                setKeyValue={setKeyValue}
                selectedProject={selectedProject}
                setChecked={setChecked}
                checked={checked}
                setRestrict={setRestrict}
                restrict={restrict}
                pipelineData={pipelineData}
                uniquePipeId={uniquePipeId}
                isWriteAccess={isWriteAccess}
                editingSowRecord={editingSowRecord}
                setEditingSowRecord={setEditingSowRecord}
              />
              <div
                onClick={
                  !isWriteAccess && selectedProject?.id
                    ? () => {
                        if (!pqtElements?.length) onFetchPqtElements();
                        onFetchProjectPqtReport(get(selectedProject, 'id'));
                        setIsPqtReportModalVisible(true);
                      }
                    : undefined
                }
                className="new-pipeline"
                style={{ marginLeft: 16 }}
              >
                <Text className="ps-3 add-new-pipeline">
                  <span className="icon-22">
                    <svg>
                      <use xlinkHref="#add" />
                    </svg>
                  </span>
                  Add PQT Activities
                </Text>
              </div>
            </div>
            <div className="custom-line" />
          </div>
          <div
            className="flex-fill border-right overflow-auto"
            style={{
              // height: '40vh',
              height: 'calc(100vh - 180px)',
            }}
          >
            <div className="d-flex flex-column h-100">
              <div className="flex-auto overflow-hidden">
                <PipelineDetails
                  pipelineData={filteredData}
                  setIsModalVisible={setIsModalVisible}
                  isModalVisible={isModalVisible}
                  editDetailsForm={editDetailsForm}
                  onSave={onSave}
                  setEditValue={setEditValue}
                  setKeyValue={setKeyValue}
                  setChecked={setChecked}
                  setRestrict={setRestrict}
                  restrict={restrict}
                  setUniquePipeId={setUniquePipeId}
                  isWriteAccess={isWriteAccess}
                  setEditingSowRecord={setEditingSowRecord}
                />
                <div className="mt-3">
                  <Title level={5} className="mb-2">PQT Planning</Title>
                  {pqtReport ? (
                    <div className="d-flex align-items-center justify-content-between py-2 px-2" style={{ background: 'var(--bg-secondary, #f5f5f5)', borderRadius: 4 }}>
                      <div>
                        <Text strong>{pqtReport.name}</Text>
                        {pqtReport.description && (
                          <div className="text-muted" style={{ fontSize: 12 }}>{pqtReport.description}</div>
                        )}
                      </div>
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => !isWriteAccess && setIsPqtReportModalVisible(true)}
                        disabled={!!isWriteAccess}
                      >
                        Edit
                      </Button>
                    </div>
                  ) : (
                      <Text type="secondary">No PQT planning. Click &quot;Add PQT Activities&quot; to create.</Text>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <AddPQTReportModal
        visible={isPqtReportModalVisible}
        onCancel={() => setIsPqtReportModalVisible(false)}
        onSave={handleSavePqtReport}
        pqtElements={pqtElements}
        existingReport={pqtReport}
        loading={isPqtReportLoading}
        isReportFetching={isReportFetching}
        isWriteAccess={isWriteAccess}
        onCreatePqtElement={onCreatePqtElement}
      />
      <div
        className="flex-fill custom-fill overflow-auto"
        style={{ maxWidth: '20%' }}
      >
        <div className="d-flex flex-column h-100">
          <div className="flex-auto">
            <div className="d-flex mb-3 ps-3 align-items-center tab-content-title">
              <Title
                className="mb-0"
                level={4}
              >
                Other Details
              </Title>
            </div>
            <div className="custom-line" />
          </div>

          <div className="flex-fill">
            <div className="d-flex flex-column h-100">
              <div className="flex-auto overflow-hidden">
                <div className="py-3 other-details px-3">
                  <Row
                    justify="start"
                    className="detail-rows"
                  >
                    <Col span={9}>
                      <Text>Contract #</Text>
                    </Col>
                    <Col span={15}>
                      <Text>:&nbsp;&nbsp;{selectedProject ? selectedProject.contractId : ''}</Text>
                    </Col>
                  </Row>
                  <Row
                    justify="start"
                    className="detail-rows"
                  >
                    <Col span={9}>
                      <Text>CRCE Ref #</Text>
                    </Col>
                    <Col span={15}>
                      <Text>:&nbsp;&nbsp;{selectedProject ? selectedProject.pihRefId : ''}</Text>
                    </Col>
                  </Row>
                  <Row
                    justify="start"
                    className="detail-rows"
                  >
                    <Col span={9}>
                      <Text>Contractor</Text>
                    </Col>
                    <Col span={15}>
                      <Text>:&nbsp;&nbsp;{selectedProject ? selectedProject.contractor : ''}</Text>
                    </Col>
                  </Row>
                  <Row
                    justify="start"
                    className="detail-rows"
                  >
                    <Col span={9}>
                      <Text>Location</Text>
                    </Col>
                    <Col span={15}>
                      <Text>:&nbsp;&nbsp;{selectedProject ? selectedProject.location : ''}</Text>
                    </Col>
                  </Row>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = ({ sow, equip, projectPqtReportsReducer }) => {
  const sowDetails = get(sow, 'sowData', []);
  const projectTeamMembersAccessList = get(equip, 'projectTeamMembersAccessList', []);
  const pqtElements = get(projectPqtReportsReducer, 'pqtElements', []);
  const pqtReport = get(projectPqtReportsReducer, 'report', null);
  const isPqtReportLoading = get(projectPqtReportsReducer, 'isUpdateLoading', false);
  const isReportFetching = get(projectPqtReportsReducer, 'isReportLoading', false);
  return {
    sowDetails,
    projectTeamMembersAccessList,
    pqtElements,
    pqtReport,
    isPqtReportLoading,
    isReportFetching,
  };
};

const mapDispatchToProps = {
  onFetchSowDetails: fetchSowDetails,
  onAddSowDetails: addSowDetails,
  onUpdateSowDetails: updateSowDetails,
  onFetchPqtElements: fetchPqtElements,
  onFetchProjectPqtReport: fetchProjectPqtReport,
  onUpdateProjectPqtReport: updateProjectPqtReport,
  onCreateProjectPqtReport: createProjectPqtReport,
  onCreatePqtElement: createPqtElement,
};

export default connect(mapStateToProps, mapDispatchToProps)(Details);
